import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { Box, CircularProgress } from '@mui/material'
import {
  fetchMerchantToken,
  getCachedMerchantToken,
  MerchantTokenInfo
} from 'api/axios'
import {
  AuthContext,
  AuthContextType,
  useAuthContext,
  User
} from 'context/AuthContext'
import {
  PermissionsContext,
  PermissionsContextType
} from 'context/PermissionsContext'
import jwtDecode from 'jwt-decode'
import { getContextMerchantUuid } from 'lib/merchant-group'
import { decodePermissions } from 'lib/permissions/bitmap'
import { parseCookies } from 'nookies'

type CompanySession = Record<string, any>

// Sessão (to_json_auth) da empresa escolhida no formulário. null fora da visão
// multi-empresas ou quando a empresa é a própria empresa de contexto.
const CompanySessionContext = createContext<CompanySession | null>(null)

// Marca a árvore de um formulário ligado a uma empresa do grupo (mesmo quando
// é a própria empresa de contexto), para o CreateGate saber de qual empresa é
// o bloqueio de criação.
const CompanyFormContext = createContext(false)

/** Está dentro de um formulário com empresa escolhida (visão multi-empresas). */
export const useInCompanyForm = () => useContext(CompanyFormContext)

// Cookies da sessão que os formulários leem direto, com o campo equivalente do
// payload do login. Só os que dependem da empresa.
const SESSION_COOKIE_FIELDS: Record<string, string> = {
  'nextauth.days_to_issue_billing': 'days_to_issue_billing',
  'nextauth.issue_billing_type': 'issue_billing_type',
  'nextauth.limit_days_for_payment': 'limit_days_for_payment',
  'nextauth.default_days_to_report': 'default_days_to_report',
  'nextauth.document': 'document',
  'nextauth.email': 'email',
  'nextauth.merchant_email': 'merchant_email',
  'nextauth.user': 'name',
  'nextauth.merchant_user_name': 'merchant_user_name',
  'nextauth.merchant_user_uuid': 'merchant_user_uuid',
  'nextauth.uuid': 'uuid',
  'nextauth.subdomain': 'subdomain',
  'nextauth.current_plan': 'current_plan',
  'nextauth.installment_billings': 'installment_billings',
  'nextauth.nfse': 'nfse',
  'nextauth.pro': 'pro',
  'nextauth.cora_bank': 'cora_bank',
  'nextauth.cora_enabled': 'cora_enabled',
  'nextauth.custom_invoice_domain': 'custom_invoice_domain',
  'nextauth.custom_portal_domain': 'custom_portal_domain',
  'nextauth.free_tier': 'free_tier',
  'nextauth.stage': 'stage',
  'nextauth.status': 'status'
}

/**
 * SC-10999: `parseCookies()` para formulários. Dentro de um formulário ligado a
 * outra empresa do grupo, os valores que dependem da empresa (prazos de
 * emissão e de pagamento, documento etc.) saem da sessão dela; fora disso são
 * os cookies de sempre.
 */
export const useSessionCookies = (): Record<string, string> => {
  const session = useContext(CompanySessionContext)
  const cookies = parseCookies()

  if (!session) return cookies

  const overrides = Object.entries(SESSION_COOKIE_FIELDS).reduce<
    Record<string, string>
  >((acc, [cookie, field]) => {
    const value = session[field]
    if (value !== undefined && value !== null) acc[cookie] = String(value)
    return acc
  }, {})

  return { ...cookies, ...overrides }
}

// Mesmo formato do `setUser` do login (handleWithLogin).
const userFromSession = (session: CompanySession, fallback: User | null) =>
  ({
    ...(fallback || {}),
    uuid: session.uuid,
    name: session.name,
    logo: session.logo_url || undefined,
    automatic_transfer: session.automatic_transfer,
    role: session.role ?? fallback?.role,
    status: session.status,
    pro: !!session.pro,
    email: session.email,
    merchant_email: session.merchant_email || undefined,
    document: session.document,
    phone: session.phone || undefined,
    installment_billings: !!session.installment_billings,
    merchant_user_name: session.merchant_user_name,
    nfse: !!session.nfse,
    portal_personalized: !!session.portal_personalized,
    stage: session.stage,
    current_plan: session.current_plan,
    trial_days_left: session.trial_days_left,
    trial_ends_at: session.trial_ends_at,
    subdomain: session.subdomain,
    cora_bank: !!session.cora_bank,
    cora_enabled: !!session.cora_enabled,
    custom_invoice_domain: session.custom_invoice_domain,
    custom_portal_domain: session.custom_portal_domain
  } as User)

type CompanySessionProviderProps = {
  // Empresa escolhida no formulário (undefined fora da visão multi-empresas)
  companyUuid?: string
  children: React.ReactNode
}

/**
 * SC-10999: na visão multi-empresas, o formulário de criação ligado a uma
 * empresa diferente da de contexto enxerga a sessão DESSA empresa: `user`
 * (assinante do contrato), `capabilities`, `creationBlocked` e os cookies lidos
 * por `useSessionCookies`. A sessão vem do `/v1/auth/merchant_token`, no mesmo
 * cache do token, e o formulário só monta depois dela para os valores iniciais
 * já nascerem certos.
 */
const CompanySessionProvider = ({
  companyUuid,
  children
}: CompanySessionProviderProps) => {
  const auth = useAuthContext()
  const needsSession = !!companyUuid && companyUuid !== getContextMerchantUuid()

  // O cache do token é a fonte: lido a cada render, a sessão já vale no mesmo
  // render em que a empresa muda. O estado só força o render quando a busca
  // termina.
  const [, setLoadedUuid] = useState<string | undefined>()
  const [failedUuid, setFailedUuid] = useState<string | undefined>()
  const info: MerchantTokenInfo | undefined = needsSession
    ? getCachedMerchantToken(companyUuid)
    : undefined
  const failed = !!companyUuid && failedUuid === companyUuid

  useEffect(() => {
    if (!needsSession || !companyUuid) return

    let active = true

    fetchMerchantToken(companyUuid)
      .then(() => active && setLoadedUuid(companyUuid))
      // Sem a sessão o formulário segue com os dados da empresa de contexto;
      // o envio vai com o token da empresa escolhida e a API responde o erro.
      .catch(() => active && setFailedUuid(companyUuid))

    return () => {
      active = false
    }
  }, [companyUuid, needsSession])

  const session = needsSession ? info?.session || null : null

  const value = useMemo<AuthContextType | null>(() => {
    if (!session) return null

    return {
      ...auth,
      user: userFromSession(session, auth.user),
      capabilities: session.capabilities ?? auth.capabilities,
      creationBlocked:
        session.creation_blocked ?? info?.creation_blocked ?? false
    }
  }, [auth, session, info?.creation_blocked])

  // Permissões da empresa escolhida. Fora do formulário a visão do grupo usa a
  // interseção das empresas (token de grupo); dentro dele, o que vale é o que o
  // usuário pode na empresa em que o registro vai ser criado.
  const permissionsValue = useMemo<PermissionsContextType | null>(() => {
    if (!companyUuid) return null
    const fromToken = (token?: string) => {
      try {
        return token
          ? decodePermissions(
              jwtDecode<{ permissions: string }>(token).permissions
            )
          : null
      } catch {
        return null
      }
    }
    const permissions = needsSession
      ? info?.permissions
        ? decodePermissions(info.permissions)
        : fromToken(info?.token)
      : fromToken(parseCookies()['nextauth.token'])
    if (!permissions) return null

    return {
      permissions,
      hasPermission: (permission) => permissions.includes(permission),
      hasAnyPermission: (list) => list.some((p) => permissions.includes(p)),
      hasAllPermissions: (list) => list.every((p) => permissions.includes(p))
    }
  }, [companyUuid, needsSession, info?.permissions, info?.token])

  const markCompanyForm = (content: React.ReactNode) => {
    if (!companyUuid) return <>{content}</>

    const marked = (
      <CompanyFormContext.Provider value>{content}</CompanyFormContext.Provider>
    )
    return permissionsValue ? (
      <PermissionsContext.Provider value={permissionsValue}>
        {marked}
      </PermissionsContext.Provider>
    ) : (
      marked
    )
  }

  if (!needsSession) return markCompanyForm(children)

  if (!value) {
    // API sem `session` no merchant_token, ou falha na busca: segue com os
    // dados da empresa de contexto em vez de travar o formulário.
    if (failed || info) return markCompanyForm(children)

    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return markCompanyForm(
    <AuthContext.Provider value={value}>
      <CompanySessionContext.Provider value={session}>
        {children}
      </CompanySessionContext.Provider>
    </AuthContext.Provider>
  )
}

export default CompanySessionProvider
